import type {
  CustomerSubAccountAssignableAccountsApiResponse,
  CustomerSubAccountAvailableRole,
  CustomerSubAccountAvailableRolesApiResponse,
  CustomerSubAccountPermissionNode,
  CustomerSubAccountPermissionTreeApiNode,
  CustomerSubAccountPermissionTreeApiResponse,
  CustomerSubAccountRole,
  CustomerSubAccountRoleDetail,
  CustomerSubAccountRoleDetailApiResponse,
  CustomerSubAccountRoleListApiResponse,
  CustomerSubAccountRoleListQuery,
  CustomerSubAccountRoleListResult,
  CustomerSubAccountUser,
  CustomerSubAccountUserDetail,
  CustomerSubAccountUserDetailApiResponse,
  CustomerSubAccountUserListApiResponse,
  CustomerSubAccountUserListQuery,
  CustomerSubAccountUserListResult,
  ResetCustomerSubAccountPasswordParams,
  SaveCustomerSubAccountRoleParams,
  SaveCustomerSubAccountUserParams,
  UpdateCustomerSubAccountRoleStatusParams,
} from './type/customer-sub-account'

import type { PagingRequest } from '#/api/type'

import { requestClient } from '#/api'
import { defineApi } from '#/api/define-api'
import { encryptPassword } from '#/utils'

export const customerSubAccountApi = defineApi('operation', ({ prefix }) => {
  function createRole(params: SaveCustomerSubAccountRoleParams) {
    return requestClient.post(prefix('custRole'), {
      companyId: params.companyId,
      permissionIds: params.permissionIds.map(Number),
      roleDesc: params.description,
      roleName: params.roleName,
      status: params.status,
    })
  }

  function getRoleList(
    params: PagingRequest<CustomerSubAccountRoleListQuery>,
  ): Promise<CustomerSubAccountRoleListResult> {
    return requestClient
      .post<CustomerSubAccountRoleListApiResponse>(prefix('custRole/query'), {
        condition: {
          companyName: params.condition.companyName,
          customerId: params.condition.customerId
            ? Number(params.condition.customerId)
            : undefined,
          roleName: params.condition.roleName,
          status: params.condition.status,
        },
        orderBy: params.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
      })
      .then((data) => ({
        list: data.list.map(
          (item): CustomerSubAccountRole => ({
            boundUserCount: item.userCount,
            companyId: item.companyId,
            customerId: String(item.customerId),
            customerName: item.companyName,
            description: item.roleDesc,
            id: String(item.id),
            roleName: item.roleName,
            status: item.status,
            updatedAt: item.updatedTime,
          }),
        ),
        total: data.total,
      }))
  }

  function getRoleDetail(params: {
    companyId: number
    id: string
  }): Promise<CustomerSubAccountRoleDetail> {
    return requestClient
      .get<CustomerSubAccountRoleDetailApiResponse>(
        prefix(`custRole/${params.id}`),
        { params: { companyId: params.companyId } },
      )
      .then((data) => {
        const mapPermissionNode = (
          node: CustomerSubAccountPermissionTreeApiNode,
        ): CustomerSubAccountPermissionNode => ({
          checked: node.checked,
          children: node.children?.map(mapPermissionNode),
          disabled: node.disabled,
          id: String(node.id),
          title: node.permissionName,
        })

        return {
          companyId: Number(data.companyId),
          customerId: String(data.customerId),
          description: data.roleDesc,
          id: String(data.id),
          permissionIds: data.permissionIds.map(String),
          permissionTree: data.menuTree.map((node) => mapPermissionNode(node)),
          roleName: data.roleName,
          status: data.status,
        }
      })
  }

  function getPermissionTree(companyId: number) {
    return requestClient
      .get<CustomerSubAccountPermissionTreeApiResponse>(
        prefix('custRole/permTree'),
        { params: { companyId } },
      )
      .then((data) => {
        const mapPermissionNode = (
          node: CustomerSubAccountPermissionTreeApiNode,
        ): CustomerSubAccountPermissionNode => ({
          checked: node.checked,
          children: node.children?.map(mapPermissionNode),
          disabled: node.disabled,
          id: String(node.id),
          title: node.permissionName,
        })

        return data.menuTree.map((node) => mapPermissionNode(node))
      })
  }

  function updateRoleStatus(params: UpdateCustomerSubAccountRoleStatusParams) {
    return requestClient.post(prefix('custRole/status'), {
      companyId: params.companyId,
      id: Number(params.id),
      status: params.status,
    })
  }

  function updateRole(params: SaveCustomerSubAccountRoleParams) {
    return requestClient.post(prefix('custRole/update'), {
      companyId: params.companyId,
      id: Number(params.id),
      permissionIds: params.permissionIds.map(Number),
      roleDesc: params.description,
      roleName: params.roleName,
      status: params.status,
    })
  }

  function getAssignableAccounts(companyId: number) {
    return requestClient
      .get<CustomerSubAccountAssignableAccountsApiResponse>(
        prefix('subAcct/accounts'),
        { params: { companyId } },
      )
      .then((data) => ({
        balanceAccounts: data.balanceAccounts.map((item) => ({
          accountId: item.accountId,
          id: item.accountId,
          mediaPlatform: item.mediaPlatform,
          name: item.accountName,
        })),
        normalAccounts: data.normalAccounts.map((item) => ({
          accountId: item.accountId,
          id: String(item.refId),
          mediaPlatform: item.mediaPlatform,
          name: item.accountName,
          refId: item.refId,
        })),
      }))
  }

  function getAvailableRoles(
    companyId: number,
  ): Promise<CustomerSubAccountAvailableRole[]> {
    return requestClient
      .get<CustomerSubAccountAvailableRolesApiResponse>(
        prefix('subAcct/roles'),
        { params: { companyId } },
      )
      .then((data) =>
        data.list.map((item) => ({
          id: String(item.id),
          roleName: item.roleName,
        })),
      )
  }

  function getUserDetail(params: {
    companyId: number
    id: string
  }): Promise<CustomerSubAccountUserDetail> {
    return requestClient
      .get<CustomerSubAccountUserDetailApiResponse>(
        prefix(`subAcct/${params.id}`),
        { params: { companyId: params.companyId } },
      )
      .then((data) => ({
        companyId: Number(data.companyId),
        customerId: String(data.customerId),
        id: String(data.id),
        loginAccount: data.username,
        name: data.realName,
        normalAccountIds: data.assetRefIds?.map(String) ?? [],
        roleId: String(data.roleId),
        status: data.status,
        walletAccountIds: data.balanceAccountIds ?? [],
      }))
  }

  function createUser(params: SaveCustomerSubAccountUserParams) {
    return requestClient.post(prefix('subAcct'), {
      assetRefIds: params.normalAccountIds.map(Number),
      balanceAccountIds: params.walletAccountIds,
      companyId: params.companyId,
      password: params.password
        ? encryptPassword(params.password, false)
        : undefined,
      realName: params.name,
      roleId: Number(params.roleId),
      status: params.status,
      username: params.loginAccount,
    })
  }

  function getUserList(
    params: PagingRequest<CustomerSubAccountUserListQuery>,
  ): Promise<CustomerSubAccountUserListResult> {
    return requestClient
      .post<CustomerSubAccountUserListApiResponse>(prefix('subAcct/query'), {
        condition: {
          companyName: params.condition.companyName,
          customerId: params.condition.customerId
            ? Number(params.condition.customerId)
            : undefined,
          status: params.condition.status,
          username: params.condition.username,
        },
        orderBy: params.orderBy,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
      })
      .then((data) => ({
        list: data.list.map(
          (item): CustomerSubAccountUser => ({
            companyId: item.companyId,
            customerId: String(item.customerId),
            customerName: item.companyName,
            id: String(item.id),
            lastLoginAt: item.lastLogin,
            loginAccount: item.username,
            name: item.realName,
            normalAccountCount: item.normalAccounts,
            normalAccountIds: [],
            roleId: String(item.roleId),
            roleName: item.roleName,
            status: item.status,
            walletAccountCount: item.balanceAccounts,
            walletAccountIds: [],
          }),
        ),
        total: data.total,
      }))
  }

  function resetUserPassword(params: ResetCustomerSubAccountPasswordParams) {
    return requestClient.post(prefix('subAcct/resetPwd'), {
      companyId: params.companyId,
      confirmNewPassword: encryptPassword(params.confirmPassword, false),
      id: Number(params.id),
      newPassword: encryptPassword(params.password, false),
    })
  }

  function updateUser(params: SaveCustomerSubAccountUserParams) {
    return requestClient.post(prefix('subAcct/update'), {
      assetRefIds: params.normalAccountIds.map(Number),
      balanceAccountIds: params.walletAccountIds,
      companyId: params.companyId,
      id: Number(params.id),
      realName: params.name,
      roleId: Number(params.roleId),
      status: params.status,
    })
  }

  return {
    createRole,
    createUser,
    getAssignableAccounts,
    getAvailableRoles,
    getPermissionTree,
    getRoleDetail,
    getRoleList,
    getUserDetail,
    getUserList,
    resetUserPassword,
    updateRole,
    updateRoleStatus,
    updateUser,
  }
})
